import type { ReactNode } from "react"
import type { editor } from "monaco-editor"
import Collapsible from "@/v2-ui/collapsible"
import Icon from "@/v2-ui/icon"
import { useLayerServiceMetadata } from "@/v2-backoffice-client/layer/service/layer.service.metadata.hooks"
import LayerUpdateInspectorGuidedScale from "@/v2-backoffice-client/layer/update/inspector/LayerUpdateInspectorGuidedScale"
import LayerUpdateInspectorGuidedPopup from "@/v2-backoffice-client/layer/update/inspector/LayerUpdateInspectorGuidedPopup"
import LayerUpdateInspectorGuidedRenderer from "@/v2-backoffice-client/layer/update/inspector/LayerUpdateInspectorGuidedRenderer"
import LayerUpdateInspectorGuidedFilters from "@/v2-backoffice-client/layer/update/inspector/LayerUpdateInspectorGuidedFilters"
import {
  parseDraftArray,
  parseDraftObject,
} from "@/v2-backoffice-client/layer/update/inspector/layer.update.inspector.guided.utils"
import type { LayerEditorField } from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"

export type LayerUpdateInspectorGuidedProps = {
  editor: editor.IStandaloneCodeEditor
  field: LayerEditorField
  /** Current document text (the inspector's live snapshot). */
  text: string
  /** The service url resolved from the layer's Configuration. */
  serviceUrl: string | null
}

/**
 * The inspector's guided widgets: metadata-aware tools that answer easy
 * configuration questions with clicks (which popup fields, which filters,
 * what renderer, which scales) while the JSON stays the source of truth —
 * every widget writes AST-preserving, undoable edits. Shared by the update
 * sheet and the create sheet, so both flows feel identical.
 */
function LayerUpdateInspectorGuided(props: LayerUpdateInspectorGuidedProps) {
  const { editor: editorInstance, field, text, serviceUrl } = props

  // Scale sliders work without metadata; everything else needs the probe.
  const wantsMetadata = field === "Configuration" || field === "Filters"
  const { metadata, error, isLoading } = useLayerServiceMetadata(
    wantsMetadata ? serviceUrl : null,
  )

  if (field !== "Configuration" && field !== "Filters") return null

  const configuration = parseDraftObject(text)
  const filters = parseDraftArray(text)

  const sections: Array<{ key: string; title: string; body: ReactNode }> = []

  if (field === "Configuration" && configuration) {
    sections.push({
      key: "scale",
      title: "Visibility scale",
      body: (
        <LayerUpdateInspectorGuidedScale
          editor={editorInstance}
          configuration={configuration}
        />
      ),
    })
    if (metadata && metadata.fields.length > 0) {
      sections.push({
        key: "popup",
        title: "Popup fields",
        body: (
          <LayerUpdateInspectorGuidedPopup
            editor={editorInstance}
            configuration={configuration}
            metadata={metadata}
          />
        ),
      })
    }
    if (metadata?.geometryType) {
      sections.push({
        key: "renderer",
        title: "Renderer presets",
        body: (
          <LayerUpdateInspectorGuidedRenderer
            editor={editorInstance}
            metadata={metadata}
          />
        ),
      })
    }
  }

  if (field === "Filters" && filters && metadata) {
    sections.push({
      key: "filters",
      title: "Filter controls",
      body: (
        <LayerUpdateInspectorGuidedFilters
          editor={editorInstance}
          filters={filters}
          metadata={metadata}
        />
      ),
    })
  }

  if (sections.length === 0 && !isLoading && !error) return null

  return (
    <div className="mb-2 flex shrink-0 flex-col gap-1 border-b pb-2">
      {isLoading && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <span className="inline-block size-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Reading the service metadata…
        </p>
      )}

      {error && (
        <p
          className="flex items-start gap-1.5 text-muted-foreground"
          title={error}
        >
          <Icon src="triangle-warning" size={12} className="mt-px shrink-0" />
          Service metadata unavailable — guided widgets are limited.
        </p>
      )}

      {sections.map((section) => (
        <Collapsible key={section.key} defaultOpen className="group/guided">
          <Collapsible.Trigger className="flex w-full items-center justify-between py-1 text-xs font-medium">
            {section.title}
            <Icon
              src="angle-down"
              size={10}
              className="transition-transform group-data-[state=closed]/guided:-rotate-90"
            />
          </Collapsible.Trigger>
          <Collapsible.Content className="pb-1.5 pt-0.5">
            {section.body}
          </Collapsible.Content>
        </Collapsible>
      ))}
    </div>
  )
}

export default LayerUpdateInspectorGuided
